import {
    DlpConfigurationModel, DlpProjectModel, DlpScopeModel
} from '../services/DlpConfiguration/DlpConfigurationService';

import { LabelIssueCode, validateDlpLabels } from './dlpLabelHierarchy';

export type DlpIssueSeverity = 'error' | 'warning';
export type DlpIssueTarget = 'projects' | 'scopes' | 'profiles';
export type DlpScopeStatus = 'active' | 'future' | 'past';

export interface DlpIssue {
    id: string;
    severity: DlpIssueSeverity;
    rule: string;
    // Translation key under "dlpConfiguration.rules", interpolated with params
    messageKey: string;
    params: Record<string, string | number>;
    target: DlpIssueTarget;
    targetId: string | number;
}

export const profileKey = (projectId: string, profileName: string): string => `${projectId}::${profileName}`;

export function scopeStatus(scope: Pick<DlpScopeModel, 'start_from' | 'ended_at'>): DlpScopeStatus {
    const today = new Date().toISOString().slice(0, 10);
    if (scope.start_from && scope.start_from > today) return 'future';
    if (scope.ended_at && scope.ended_at < today) return 'past';
    return 'active';
}

export function countIssues(issues: DlpIssue[]) {
    return {
        errors: issues.filter(i => i.severity === 'error').length,
        warnings: issues.filter(i => i.severity === 'warning').length
    };
}

// Client-side mirror of the DLP sender's config checks, so problems show up while editing.
export function validateDlpConfiguration(
    { projects, scopes, scopeBrands, profiles }: DlpConfigurationModel,
    brandName: (brandId: string) => string
): DlpIssue[] {
    const issues: DlpIssue[] = [];
    const add = (severity: DlpIssueSeverity, rule: string, params: Record<string, string | number>, target: DlpIssueTarget, targetId: string | number, messageKey: string = rule) =>
        issues.push({ id: `${rule}-${targetId}-${issues.length}`, severity, rule, messageKey, params, target, targetId });

    const projectById: Record<string, DlpProjectModel> = Object.fromEntries(projects.map(p => [p.id, p]));

    // --- Projects ---
    const seenIds = new Set<string>();
    const codeToProject: Record<string, DlpProjectModel> = {};
    projects.forEach(p => {
        const params = { project: p.id };
        if (seenIds.has(p.id)) add('error', 'duplicate_project_id', params, 'projects', p.id);
        seenIds.add(p.id);

        const stratified = p.group_by.includes('label');
        if (stratified && !p.label_field) add('error', 'label_stratified_missing_label_field', params, 'projects', p.id);
        if (!stratified && p.label_field) add('error', 'label_field_without_label_stratification', params, 'projects', p.id);

        if (p.is_active && (!p.dlp_labels || p.dlp_labels.length === 0)) add('error', 'missing_dlp_labels', params, 'projects', p.id);

        const labelIds = (p.dlp_labels || []).map(l => l.id);
        const dup = labelIds.find((id, i) => id && labelIds.indexOf(id) !== i);
        if (dup) add('error', 'duplicate_label_id', { ...params, label: dup }, 'projects', p.id);
        if ((p.dlp_labels || []).some(l => !l.id.trim() || !l.name.trim())) add('error', 'invalid_dlp_labels', params, 'projects', p.id);
        const hierarchyRules: Partial<Record<LabelIssueCode, string>> = { missingParent: 'label_missing_parent', selfParent: 'label_self_parent', cycle: 'label_cycle', tooDeep: 'label_too_deep' };
        validateDlpLabels(p.dlp_labels || []).forEach(issue => {
            const rule = hierarchyRules[issue.code];
            if (rule) add('error', rule, { ...params, label: issue.label, parent: issue.parent ?? '' }, 'projects', p.id);
        });

        if (p.dlp_project_code) {
            const other = codeToProject[p.dlp_project_code];
            if (!other) codeToProject[p.dlp_project_code] = p;
            else if (JSON.stringify(other.dlp_labels) !== JSON.stringify(p.dlp_labels)) {
                add('error', 'conflicting_dlp_labels_for_code', { ...params, other: other.id, code: p.dlp_project_code }, 'projects', p.id);
            }
        }
    });

    // --- Scopes ---
    scopes.forEach(s => {
        const params = { scope: s.name, project: s.project_id };
        const project = projectById[s.project_id];
        if (!project) { add('error', 'scope_orphan_project', params, 'scopes', s.scope_id); return; }

        if (s.ended_at && s.start_from && s.ended_at < s.start_from) add('error', 'scope_start_after_end', params, 'scopes', s.scope_id);

        (s.channels || []).forEach(c => {
            if (!project.channels.includes(c)) add('error', 'scope_channel_not_in_project_channels', { ...params, channel: c }, 'scopes', s.scope_id);
        });

        const profileExists = profiles.some(p => p.profile_name === s.quota_profile && p.project_id === s.project_id);
        if (!s.quota_profile || !profileExists) add('error', 'missing_quota_profile', { ...params, profile: s.quota_profile || '-' }, 'scopes', s.scope_id);

        const hasBrands = scopeBrands.some(sb => sb.scope_id === s.scope_id);
        if (!s.all_brands && !hasBrands) add('error', 'scope_missing_brand', params, 'scopes', s.scope_id);

        if (s.all_brands && project.group_by.length > 0) add('error', 'wildcard_scope_on_capped_project', params, 'scopes', s.scope_id);
    });

    // A brand in more than one scope of the same project is ambiguous:
    // quota resolution needs exactly one scope per brand per project.
    const brandScopes = new Map<string, number[]>();
    scopeBrands.forEach(sb => {
        const scope = scopes.find(s => s.scope_id === sb.scope_id);
        if (!scope) return;
        const key = `${scope.project_id}::${sb.brand_id}`;
        brandScopes.set(key, [...(brandScopes.get(key) ?? []), sb.scope_id]);
    });
    brandScopes.forEach((scopeIds, key) => {
        if (scopeIds.length < 2) return;
        const brand = brandName(key.split('::')[1]);
        scopeIds.forEach(sid => add('error', 'duplicate_brand_in_scopes', { brand }, 'scopes', sid));
    });

    const wildcardsByProject = new Map<string, number[]>();
    scopes.filter(s => s.all_brands).forEach(s => {
        wildcardsByProject.set(s.project_id, [...(wildcardsByProject.get(s.project_id) ?? []), s.scope_id]);
    });
    wildcardsByProject.forEach(scopeIds => {
        if (scopeIds.length > 1) scopeIds.forEach(sid => add('error', 'duplicate_brand_in_scopes', {}, 'scopes', sid, 'duplicate_all_brands_scope'));
    });

    // --- Quota profiles ---
    const keys = Array.from(new Set(profiles.map(p => profileKey(p.project_id, p.profile_name))));
    keys.forEach(key => {
        const [projectId, name] = key.split('::');
        const project = projectById[projectId];
        if (!project) return;
        const params = { profile: name, project: projectId };
        const rows = profiles.filter(p => p.project_id === projectId && p.profile_name === name);
        const isReferenced = scopes.some(s => s.project_id === projectId && s.quota_profile === name);

        const seenRow = new Set<string>();
        rows.forEach(r => {
            const k = `${r.channel || 'ANY'}::${r.label || 'ANY'}`;
            if (seenRow.has(k)) add('error', 'duplicate_profile_row', { ...params, channel: r.channel || 'any', label: r.label || 'any' }, 'profiles', key);
            seenRow.add(k);
        });

        if (!isReferenced) add('warning', 'profile_not_referenced', params, 'profiles', key);

        rows.forEach(r => {
            if (r.channel && !project.channels.includes(r.channel)) add('error', 'profile_channel_not_in_project_channels', { ...params, channel: r.channel }, 'profiles', key);
        });

        if (project.group_by.length > 0) {
            if (rows.some(r => r.max_docs === null || r.max_docs === undefined)) add('error', 'null_max_docs_on_stratified_project', params, 'profiles', key);
        } else if (rows.some(r => r.max_docs !== null && r.max_docs !== undefined)) {
            add('warning', 'max_docs_set_on_unstratified_project', params, 'profiles', key);
        }

        if (project.group_by.includes('label') && !rows.some(r => r.label)) add('error', 'label_stratified_with_no_labelled_profile_row', params, 'profiles', key);

        if (rows.length > 0 && rows.every(r => (r.max_docs ?? 0) === 0)) add('warning', 'profile_sends_nothing', params, 'profiles', key);
    });

    return issues;
}
